import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, ChevronDown, MoreHorizontal } from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useGetCollaborationQuery } from '@/redux/marketerApi'
import { ContentReview, Pill, PlatformIcon } from '@/components/workspace/ContentReview'
import { StageBody } from '@/components/workspace/StageViews'
import { RightRail } from '@/components/workspace/RightRail'
import {
  activeStageKey,
  buildStages,
  stageDate,
  stateTone,
  type Stage,
} from '@/components/workspace/stageModel'

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
const day = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

const STATUS_PILL: Record<string, { tone: 'blue' | 'green' | 'amber' | 'grey'; label: string }> = {
  accepted: { tone: 'green', label: 'In progress' },
  pending_brand: { tone: 'blue', label: 'Offer to review' },
  offered: { tone: 'amber', label: 'Offer sent' },
  rejected: { tone: 'grey', label: 'Declined' },
  expired: { tone: 'grey', label: 'Expired' },
}

/** The six-step tracker; tap a step to open it */
const Stepper: React.FC<{ stages: Stage[]; open: string; onPick: (key: string) => void }> = ({
  stages,
  open,
  onPick,
}) => (
  <ol className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:flex lg:items-start lg:gap-0">
    {stages.map((s, i) => (
      <li key={s.key} className="flex min-w-0 items-start gap-2 lg:flex-1">
        <button
          type="button"
          onClick={() => onPick(s.key)}
          aria-current={open === s.key ? 'step' : undefined}
          className={cn(
            'flex min-w-0 items-start gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors',
            open === s.key ? 'bg-[#F4FBEA]' : 'hover:bg-muted',
          )}
        >
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
              s.state === 'done' && 'bg-[#16A34A] text-[#FFFFFF]',
              s.state === 'needs_you' && 'bg-[#C0FD72] text-[#0F172A] ring-4 ring-[#C0FD72]/40',
              s.state === 'with_athlete' && 'border-2 border-[#16A34A] bg-card text-[#16A34A]',
              (s.state === 'upcoming' || s.state === 'closed') &&
                'border-2 border-border bg-card text-muted-foreground',
            )}
          >
            {s.state === 'done' ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
          </span>
          <span className="min-w-0">
            <span
              className={cn(
                'block text-sm font-semibold leading-tight',
                s.state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {s.label}
            </span>
            <span
              className={cn(
                'block text-xs',
                s.state === 'needs_you' ? 'font-semibold text-[#3F7A0A]' : 'text-muted-foreground',
              )}
            >
              {s.status}
            </span>
            {stageDate(s) ? (
              <span className="block text-xs text-muted-foreground">{stageDate(s)}</span>
            ) : null}
          </span>
        </button>
        {i < stages.length - 1 ? (
          <span
            aria-hidden
            className={cn(
              'mx-1 mt-5 hidden h-0.5 flex-1 lg:block',
              s.state === 'done' ? 'bg-[#16A34A]' : 'bg-border',
            )}
          />
        ) : null}
      </li>
    ))}
  </ol>
)

/**
 * One athlete's collaboration on a campaign, as one workspace: the deal at the top, a tracker of
 * its stages, and each stage below (the one that needs the brand open), with the deal details,
 * messages, brief and activity alongside.
 */
const CollaborationWorkspace: React.FC = () => {
  const { campaignId, collaborationId = '' } = useParams()
  const navigate = useNavigate()
  const {
    data: c,
    isLoading,
    isError,
    refetch,
  } = useGetCollaborationQuery(collaborationId, {
    skip: !collaborationId,
  })
  const stages = useMemo(() => (c ? buildStages(c) : []), [c])
  const [open, setOpen] = useState<string | null>(null)
  const refs = useRef<Record<string, HTMLElement | null>>({})
  const defaultKey = stages.length ? activeStageKey(stages) : null
  const openKey = open ?? defaultKey

  // Re-open on what needs attention after something changes, unless the brand picked a stage
  useEffect(() => {
    setOpen(null)
  }, [c?.updatedAt])

  if (isLoading) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-[1440px] space-y-4 p-4 sm:p-6">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    )
  }
  if (isError || !c) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-md p-6 text-center">
          <p className="text-lg font-semibold text-foreground">
            This collaboration couldn't be loaded.
          </p>
          <button
            type="button"
            onClick={() => navigate('/campaigns')}
            className="mt-3 font-semibold text-[#3F7A0A]"
          >
            Back to campaigns
          </button>
        </div>
      </MainLayout>
    )
  }

  const athleteName = c.athlete?.name ?? 'Athlete'
  const first = athleteName.split(' ')[0]
  const brand = c.campaign?.brand
  const platforms = (c.campaign?.platform ?? []).filter(Boolean)
  const pill =
    c.status === 'accepted' && stages.every((s) => s.state === 'done')
      ? { tone: 'green' as const, label: 'Complete' }
      : (STATUS_PILL[c.status] ?? { tone: 'grey' as const, label: c.status })
  const booking = (c.offerDetails as any)?.booking
  const deliverableCount =
    booking?.kind === 'rate_card' ? booking.quantity : (c.structuredDeliverables?.length ?? 0)
  const pick = (key: string) => {
    setOpen(key)
    requestAnimationFrame(() =>
      refs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    )
  }
  const back = campaignId ? `/campaign/${campaignId}` : '/campaigns'

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1440px] space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to={back}
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to campaign
          </Link>
          <div className="flex items-center gap-2">
            {c.campaignId ? (
              <Link
                to={`/campaign/${c.campaignId}`}
                className="rounded-xl border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
              >
                Campaign details
              </Link>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label="More"
                className="rounded-xl border border-border bg-card p-2 text-foreground hover:bg-muted"
              >
                <MoreHorizontal className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => navigate('manage', { relative: 'path' })}>
                  Edit terms and due dates
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate('manage?tab=files', { relative: 'path' })}
                >
                  Files
                </DropdownMenuItem>
                {c.licensingEnabled ? (
                  <DropdownMenuItem onSelect={() => navigate('licensing', { relative: 'path' })}>
                    Licensing details
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onSelect={() => navigate(`/athlete/${c.athleteId}`)}>
                  {first}'s shop
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* The campaign, the athlete and the deal */}
        <section className="space-y-5 rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
          <div className="flex flex-wrap items-center gap-4">
            {brand?.logo ? (
              <img
                src={brand.logo}
                alt=""
                className="h-14 w-14 shrink-0 rounded-xl object-cover"
              />
            ) : (
              <span
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#0EA5E9] text-lg font-bold"
                style={{ color: '#fff' }}
              >
                {(brand?.name ?? 'B').slice(0, 2).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl font-bold truncate text-foreground">
                {c.campaign?.title ?? c.title ?? 'Collaboration'}
              </h1>
              <p className="flex flex-wrap items-center gap-2 text-muted-foreground">
                {brand?.name ?? ''}
                {platforms.length ? ` · ${platforms.join(', ')}` : ''}
                <Pill tone={pill.tone}>{pill.label}</Pill>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 border-t border-border pt-4 lg:flex-row lg:items-center">
            <div className="flex min-w-0 items-center gap-3 lg:w-[320px]">
              {c.athlete?.avatar ? (
                <img
                  src={c.athlete.avatar}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-muted text-lg font-semibold text-foreground">
                  {athleteName.charAt(0)}
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold text-foreground">{athleteName}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {[c.athlete?.school, c.athlete?.sport].filter(Boolean).join(' · ')}
                </p>
                <Link
                  to={`/athlete/${c.athleteId}`}
                  className="text-sm font-semibold text-[#3F7A0A] underline-offset-2 hover:underline"
                >
                  View Content Shop →
                </Link>
              </div>
            </div>
            <dl className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-4 lg:border-l lg:border-border lg:pl-6">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {c.dealType === 'gifting'
                    ? 'Deal'
                    : c.status === 'accepted'
                      ? 'Agreed rate'
                      : 'Proposed rate'}
                </dt>
                <dd className="text-lg font-bold text-foreground">
                  {c.dealType === 'gifting' ? 'Gifting' : money(c.paymentAmount)}
                </dd>
                {deliverableCount ? (
                  <dd className="text-xs text-muted-foreground">
                    {deliverableCount} deliverable{deliverableCount === 1 ? '' : 's'}
                  </dd>
                ) : null}
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Content due
                </dt>
                <dd className="text-lg font-bold text-foreground">{day(c.contentDueDate)}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Platforms
                </dt>
                <dd className="mt-1 flex gap-1.5">
                  {platforms.length
                    ? platforms.map((p) => (
                        <PlatformIcon key={p} platform={p} className="h-6 w-6" />
                      ))
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Licensing
                </dt>
                <dd className="text-lg font-bold text-[#3F7A0A]">Included</dd>
                <dd className="text-xs text-muted-foreground">
                  {c.performance?.usage?.endsAt
                    ? `Until ${day(c.performance.usage.endsAt)}`
                    : '30 days from posting'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="border-t border-border pt-4">
            <Stepper stages={stages} open={openKey ?? ''} onPick={pick} />
          </div>
        </section>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          {/* Each stage; the one that needs attention is open */}
          <div className="min-w-0 space-y-3">
            {stages.map((s, i) => {
              const isOpen = openKey === s.key
              return (
                <section
                  key={s.key}
                  ref={(el) => {
                    refs.current[s.key] = el
                  }}
                  className={cn(
                    'scroll-mt-4 rounded-2xl border bg-card shadow-sm',
                    isOpen && s.state === 'needs_you' ? 'border-[#9BD85A]' : 'border-border',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? '__none__' : s.key)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-3 px-4 py-3.5 text-left sm:px-5"
                  >
                    <span className="text-base font-bold text-foreground">
                      {i + 1}. {s.label}
                    </span>
                    <Pill tone={stateTone(s.state) as any}>{s.status}</Pill>
                    {stageDate(s) ? (
                      <span className="hidden text-sm text-muted-foreground sm:inline">
                        {stageDate(s)}
                      </span>
                    ) : null}
                    <ChevronDown
                      className={cn(
                        'ml-auto h-5 w-5 shrink-0 text-muted-foreground transition-transform',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>
                  {isOpen ? (
                    <div className="border-t border-border px-4 py-4 sm:px-5">
                      <StageBody
                        c={c}
                        stage={s}
                        first={first}
                        refresh={() => refetch()}
                        content={
                          s.kind === 'content' && s.milestone ? (
                            <ContentReview
                              collaboration={c}
                              milestone={s.milestone}
                              athleteName={athleteName}
                              onReviewed={() => refetch()}
                            />
                          ) : null
                        }
                      />
                    </div>
                  ) : null}
                </section>
              )
            })}
          </div>

          <RightRail c={c} first={first} />
        </div>
      </div>
    </MainLayout>
  )
}

export default CollaborationWorkspace
